import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Button, EmptyState } from '@hrms/ui';

/**
 * What a page that failed to render shows, instead of the whole app going blank.
 *
 * Every page is a lazily loaded chunk (routes/pages.ts). When one cannot be fetched — a network
 * blip, or a deploy that replaced it and the once-a-minute reload in main.tsx already spent — React
 * throws, and with no boundary anywhere in the tree it unmounts everything: sidebar, top bar and
 * all. This keeps the frame on screen and offers the one fix that works, a reload. It also catches a
 * page that crashes while rendering, for the same reason.
 *
 * Give it a `key` that changes with the route (the app frame uses the pathname), so moving to
 * another page clears the error instead of the message following the person around.
 */
export class PageErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The browser console is where a developer looks; nothing here is sent anywhere.
    console.error('Page failed to render', error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <EmptyState
        icon={<TriangleAlert className="size-8" aria-hidden="true" />}
        title="This page didn't load"
        description="Check your connection, then reload. If it keeps happening, tell your administrator."
        action={
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload
          </Button>
        }
      />
    );
  }
}
